// src/pages/MapaPage.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getPersons } from '../services/api';

// Coordenadas de provincias RD
const PROVINCE_COORDS = {
  'Distrito Nacional':    [18.4861, -69.9312],
  'Santo Domingo':        [18.5001, -69.8887],
  'Santiago':             [19.4517, -70.6970],
  'La Vega':              [19.2212, -70.5290],
  'San Cristóbal':        [18.4179, -70.1066],
  'Puerto Plata':         [19.7933, -70.6880],
  'San Pedro de Macorís': [18.4539, -69.3050],
  'La Romana':            [18.4274, -68.9728],
  'Higüey':               [18.6154, -68.7078],
  'La Altagracia':        [18.6154, -68.7078],
  'Barahona':             [18.2133, -71.0981],
  'Azua':                 [18.4525, -70.7347],
  'San Juan':             [18.8056, -71.2289],
  'Moca':                 [19.3939, -70.5239],
  'Espaillat':            [19.3939, -70.5239],
  'Bonao':                [18.9447, -70.4075],
  'Monseñor Nouel':       [18.9447, -70.4075],
  'Cotuí':                [19.0533, -70.1519],
  'Sánchez Ramírez':      [19.0533, -70.1519],
  'Nagua':                [19.3797, -69.8461],
  'María Trinidad Sánchez':[19.3797, -69.8461],
  'Samaná':               [19.2061, -69.3367],
  'Hato Mayor':           [18.7642, -69.2561],
  'El Seibo':             [18.7656, -69.0397],
  'Monte Plata':          [18.8072, -69.7828],
  'Bahoruco':             [18.4944, -71.4181],
  'Independencia':        [18.5000, -71.8500],
  'Pedernales':           [18.0381, -71.7444],
  'Dajabón':              [19.5483, -71.7086],
  'Monte Cristi':         [19.8608, -71.6489],
  'Valverde':             [19.5833, -70.9833],
  'Santiago Rodríguez':   [19.4833, -71.3333],
  'Elías Piña':           [18.8758, -71.7053],
  'Peravia':              [18.2750, -70.3292],
  'Hermanas Mirabal':     [19.3733, -70.3167],
  'Duarte':               [19.3000, -70.0000],
};

const STATUS_COLOR = { URGENTE: '#FF2D2D', DESAPARECIDA: '#FF8C00', ENCONTRADA: '#00C853' };
const STATUS_LABEL = { URGENTE: 'URGENTE', DESAPARECIDA: 'Desaparecido/a', ENCONTRADA: 'Encontrado/a' };

// Obtener coords aproximadas de un caso
function getCoords(person) {
  if (person.latitude && person.longitude) return [person.latitude, person.longitude];
  const prov = person.province || person.location?.split(',')[0]?.trim();
  if (prov && PROVINCE_COORDS[prov]) {
    // Añadir pequeño offset aleatorio para no superponer markers
    const base = PROVINCE_COORDS[prov];
    return [base[0] + (Math.random() - 0.5) * 0.15, base[1] + (Math.random() - 0.5) * 0.15];
  }
  // Default: centro RD
  return [18.7357 + (Math.random() - 0.5) * 0.3, -70.1627 + (Math.random() - 0.5) * 0.3];
}

export default function MapaPage() {
  const mapRef      = useRef(null);
  const leafletMap  = useRef(null);
  const markersRef  = useRef([]);
  const [persons,   setPersons]   = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [filter,    setFilter]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [leafletLoaded, setLeafletLoaded] = useState(false);

  // Cargar Leaflet dinámicamente
  useEffect(() => {
    if (window.L) { setLeafletLoaded(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Cargar casos
  useEffect(() => {
    getPersons('?limit=200')
      .then(data => setPersons(data.persons || []))
      .catch(() => setPersons([]))
      .finally(() => setLoading(false));
  }, []);

  // Inicializar mapa
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || leafletMap.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, {
      center: [18.7357, -70.1627],
      zoom: 8,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap © CARTO',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);
    leafletMap.current = map;
  }, [leafletLoaded]);

  // Añadir markers
  useEffect(() => {
    if (!leafletMap.current || !window.L || persons.length === 0) return;
    const L = window.L;
    const map = leafletMap.current;

    // Limpiar markers anteriores
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    const filtered = filter ? persons.filter(p => p.status === filter) : persons;

    filtered.forEach(person => {
      const coords = getCoords(person);
      const color  = STATUS_COLOR[person.status] || '#888';

      const icon = L.divIcon({
        className: '',
        html: `
          <div style="
            width: 36px; height: 36px; border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            background: ${color};
            border: 2px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.5);
            display: flex; align-items: center; justify-content: center;
          ">
            <div style="
              width: 28px; height: 28px; border-radius: 50%;
              overflow: hidden; transform: rotate(45deg);
              background: #111;
            ">
              ${person.photoUrl
                ? `<img src="${person.photoUrl}" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none'" />`
                : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${color};font-size:11px;font-weight:900;">${person.name[0]}</div>`
              }
            </div>
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      });

      const marker = L.marker(coords, { icon }).addTo(map);
      marker.on('click', () => setSelected(person));
      markersRef.current.push(marker);
    });
  }, [persons, filter, leafletLoaded]);

  const filtered  = filter ? persons.filter(p => p.status === filter) : persons;
  const urgentes  = persons.filter(p => p.status === 'URGENTE').length;
  const activos   = persons.filter(p => p.status !== 'ENCONTRADA').length;

  return (
    <div style={{ minHeight: '100vh', background: '#07070B', fontFamily: "'IBM Plex Mono', monospace", display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: '#0C0C12', borderBottom: '1px solid #1A1A28', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/" style={{ color: '#555', textDecoration: 'none', fontSize: 12 }}>← Inicio</Link>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#FF2D2D', letterSpacing: 2 }}>🗺️ MAPA DE CASOS</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['', 'Todos', '#666'], ['URGENTE', '🔴 Urgentes', '#FF2D2D'], ['DESAPARECIDA', '🟠 Activos', '#FF8C00'], ['ENCONTRADA', '🟢 Encontrados', '#00C853']].map(([val, label, color]) => (
            <button key={val} onClick={() => setFilter(val)} style={{
              background: filter === val ? `${color}22` : 'transparent',
              border: `1px solid ${filter === val ? color : '#1A1A28'}`,
              borderRadius: 8, padding: '6px 14px',
              color: filter === val ? color : '#555',
              fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              fontWeight: filter === val ? 700 : 400,
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#444' }}>
          <span>🔴 <b style={{ color: '#FF2D2D' }}>{urgentes}</b> urgentes</span>
          <span>📍 <b style={{ color: '#FF8C00' }}>{activos}</b> activos</span>
          <span>Total: <b style={{ color: '#ccc' }}>{persons.length}</b></span>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>

        {/* Mapa */}
        <div style={{ flex: 1, position: 'relative' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#07070B', zIndex: 10 }}>
              <div style={{ color: '#444', fontSize: 13 }}>Cargando mapa...</div>
            </div>
          )}
          <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 65px)' }} />
        </div>

        {/* Panel lateral - caso seleccionado */}
        {selected && (
          <div style={{ width: 300, background: '#0C0C12', borderLeft: '1px solid #1A1A28', padding: 20, overflowY: 'auto', position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 1000 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: STATUS_COLOR[selected.status], padding: '3px 8px', border: `1px solid ${STATUS_COLOR[selected.status]}44`, borderRadius: 4 }}>
                {STATUS_LABEL[selected.status]}
              </span>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            {/* Foto */}
            <div style={{ width: '100%', height: 200, borderRadius: 10, overflow: 'hidden', background: '#1A1A28', marginBottom: 16 }}>
              {selected.photoUrl
                ? <img src={selected.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, color: STATUS_COLOR[selected.status] }}>
                    {selected.name[0]}
                  </div>
              }
            </div>

            <div style={{ fontSize: 16, fontWeight: 800, color: '#ddd', marginBottom: 4 }}>{selected.name}</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{selected.age} años · {selected.gender === 'M' ? 'Masculino' : selected.gender === 'F' ? 'Femenino' : 'Otro'}</div>

            <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>📍 {selected.location}</div>
            {selected.province && <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>🏛️ {selected.province}</div>}
            {selected.lastSeen && <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>📅 Visto por última vez: {selected.lastSeen}</div>}
            {selected.daysActive > 0 && (
              <div style={{ fontSize: 11, color: selected.daysActive > 10 ? '#FF6B6B' : '#888', marginBottom: 12 }}>
                ⏱️ {selected.daysActive} días desaparecido/a
              </div>
            )}

            {selected.description && (
              <div style={{ background: '#07070B', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#777', lineHeight: 1.6, marginBottom: 16 }}>
                {selected.description}
              </div>
            )}

            {selected.contact && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#444', letterSpacing: 1, marginBottom: 6 }}>CONTACTO</div>
                <a href={`tel:${selected.contact}`} style={{ display: 'block', background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 8, padding: '10px', color: '#00C853', textDecoration: 'none', fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
                  📞 {selected.contact}
                </a>
              </div>
            )}

            <Link to={`/caso/${selected.id}`} style={{ display: 'block', background: '#FF2D2D', borderRadius: 8, padding: '11px', color: '#fff', textDecoration: 'none', fontSize: 12, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>
              Ver ficha completa →
            </Link>

            <a href={`https://wa.me/?text=🚨 PERSONA DESAPARECIDA: ${selected.name}, ${selected.age} años. Visto/a en ${selected.location}. Contacto: ${selected.contact}. Caso: ${selected.caseId}`}
              target="_blank" rel="noreferrer"
              style={{ display: 'block', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 8, padding: '11px', color: '#25D366', textDecoration: 'none', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
              📲 Compartir en WhatsApp
            </a>
          </div>
        )}

        {/* Lista flotante inferior */}
        {!selected && (
          <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, background: '#0C0C12CC', backdropFilter: 'blur(10px)', border: '1px solid #1A1A28', borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 10, overflowX: 'auto', zIndex: 500 }}>
            {filtered.filter(p => p.status !== 'ENCONTRADA').slice(0, 8).map(p => (
              <div key={p.id} onClick={() => setSelected(p)} style={{ flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: '#0F0F1A', border: `1px solid ${STATUS_COLOR[p.status]}33`, borderRadius: 8, padding: '8px 12px', minWidth: 160 }}>
                <div style={{ width: 32, height: 32, borderRadius: 6, overflow: 'hidden', background: '#1A1A28', flexShrink: 0 }}>
                  {p.photoUrl
                    ? <img src={p.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: STATUS_COLOR[p.status] }}>{p.name[0]}</div>
                  }
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>{p.name}</div>
                  <div style={{ fontSize: 9, color: STATUS_COLOR[p.status] }}>{p.status} · {p.daysActive}d</div>
                </div>
              </div>
            ))}
            {filtered.filter(p => p.status !== 'ENCONTRADA').length === 0 && (
              <div style={{ fontSize: 12, color: '#444', padding: '4px 0' }}>No hay casos activos con este filtro</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
